import { useEffect } from "react";
import Map from "ol/Map";
import Feature from "ol/Feature";
import { Geometry } from "ol/geom";
import { setMapAppState } from "../../utils/mapState";
import { FeatureCollection, FeatureFromGeojson } from "../../FeaturesStore";

/**
 * isModifying 플래그가 설정된 피처들을 일괄 해제합니다.
 * 수정 모드 비활성화 시 GeoJSON 상태를 정리하는 용도입니다.
 */
export function clearIsModifyingFlags(
  features: FeatureFromGeojson[],
  updateGeoJson: (id: string, data: Omit<FeatureFromGeojson, "id">) => void
) {
  features.forEach((f) => {
    if (f.properties?.isModifying) {
      updateGeoJson(String(f.id), {
        ...f,
        properties: { ...f.properties, isModifying: false },
      });
    }
  });
}

interface UseModifyHandleSyncOptions {
  isActive: boolean;
  map: Map;
  geoJson: FeatureCollection;
  selectedFeature: FeatureFromGeojson | null | undefined;
  findFeatureById: (id: string) => Feature<Geometry> | null;
  installModify: (feature: Feature<Geometry>) => void;
  removeModify: () => void;
  getInstalledFeatureId: () => string | undefined;
  updateGeoJson: (id: string, data: Omit<FeatureFromGeojson, "id">) => void;
}

/**
 * isActive 상태와 GeoJSON의 isModifying 플래그를 기반으로
 * OL Modify 핸들의 설치/해제 및 맵 상태를 자동으로 동기화합니다.
 */
export function useModifyHandleSync({
  isActive,
  map,
  geoJson,
  selectedFeature,
  findFeatureById,
  installModify,
  removeModify,
  getInstalledFeatureId,
  updateGeoJson,
}: UseModifyHandleSyncOptions) {
  // 수정 모드 활성/비활성에 따른 맵 상태 및 isModifying 플래그 동기화
  useEffect(() => {
    setMapAppState(map, { isModifying: isActive });

    if (!isActive) {
      removeModify();
      clearIsModifyingFlags(geoJson.features, updateGeoJson);
    }
  }, [isActive, map, removeModify, geoJson, updateGeoJson]);

  // isModifying 플래그 기반으로 수정 핸들 설치
  useEffect(() => {
    if (!isActive) return;

    const modifyingTarget = geoJson.features.find(
      (f) => f.properties?.isModifying
    );

    if (!modifyingTarget) {
      const currentId = getInstalledFeatureId();
      const selectedId = selectedFeature?.id
        ? String(selectedFeature.id)
        : undefined;

      if (currentId && currentId === selectedId) return;

      if (selectedId) {
        const feature = findFeatureById(selectedId);
        if (feature) installModify(feature);
      } else {
        removeModify();
      }
      return;
    }

    const targetId = String(modifyingTarget.id);
    if (getInstalledFeatureId() === targetId) return;

    // 증분 렌더로 feature 인스턴스가 안정 유지되므로(재빌드로 사라지지 않음) 재시도 폴링 없이
    // 즉시 설치한다(D6 — 기존: OL 레이어 렌더링 지연 대비 최대 5회·50ms 재시도 워크어라운드).
    const feature = findFeatureById(targetId);
    if (feature) installModify(feature);
  }, [
    isActive,
    geoJson,
    selectedFeature,
    findFeatureById,
    installModify,
    removeModify,
    getInstalledFeatureId,
  ]);

  // selectedFeature 변경 시 수정 핸들 갱신
  useEffect(() => {
    if (!isActive) return;

    if (!selectedFeature?.id) {
      removeModify();
      return;
    }

    const feature = findFeatureById(String(selectedFeature.id));
    if (feature) installModify(feature);
  }, [isActive, selectedFeature, findFeatureById, installModify, removeModify]);

  // 수정 모드 진입 직후 selectedFeature에 즉시 핸들 설치
  useEffect(() => {
    if (!isActive || !selectedFeature?.id) return;
    const feature = findFeatureById(String(selectedFeature.id));
    if (feature) installModify(feature);
  }, [isActive, selectedFeature, findFeatureById, installModify]);
}
